<script setup>
import { computed } from 'vue';
import useSiteStore, { blockingTypeEnum } from '@vueApp/store/modules/site.js';
import IMatchEventsSkeleton from '@vueApp/components/IMatchEvents/IMatchEventsSkeleton.vue';
import IConcertSkeleton from '@vueApp/components/IConcert/IConcertSkeleton.vue';
import ISubscriptionSkeleton from '@vueApp/components/ISubscription/ISubscriptionSkeleton.vue';
import IEventsListCol from '@vueApp/components/IEventsList/IEventsListCol.vue';
import IMatchEvent from '@vueApp/components/IMatchEvents/IMatchEvent.vue';
import IEventsListRow from '@vueApp/components/IEventsList/IEventsListRow.vue';
import IConcert from '@vueApp/components/IConcert/IConcert.vue';
import ISubscription from '@vueApp/components/ISubscription/ISubscription.vue';
import EventsFilter from './EventsFilter.vue';

const store = useSiteStore();

store.searchEvents();
store.searchSubscriptions();
store.searchShows();
store.searchPerformances();

const loadingData = computed(
  () => store.getSubscriptionsState.loading
  || store.getEventsState.loading
  || store.getPerformancesState.loading,
);

const isLoadingEvents = computed(() => store.getEventsState.loading);
const isLoadingPerfomance = computed(() => store.getPerformancesState.loading);
const isLoadingSubscription = computed(() => store.getSubscriptionsState.loading);
const isLoadingShows = computed(() => store.getShowsState.loading);

const isLoadingMoreEvent = computed(() => store.getEventsState.loadingMore);
const isLoadingMorePerfomance = computed(() => store.getPerformancesState.loadingMore);
const isLoadingMoreSubscription = computed(() => store.getSubscriptionsState.loadingMore);
const isLoadingMoreShows = computed(() => store.getShowsState.loadingMore);

const isEmptyList = computed(() => !store.getEventsState.items.length
&& !store.getSubscriptionsState.items.length
&& !store.getServicesState.items.length
&& !store.getShowsState.items.length
&& !store.getPerformancesState.items.length && !loadingData.value);

const isShowEventsEnabled = computed(
  () => store.getSelectTypes.items.includes(blockingTypeEnum.MATCH),
);
const isShowSubscriptionsEnabled = computed(
  () => store.getSelectTypes.items.includes(blockingTypeEnum.SUBSCRIPTION),
);
const isShowPerfomancesEnabled = computed(
  () => store.getSelectTypes.items.includes(blockingTypeEnum.PERFORMANCE),
);
const isShowShowsEnabled = computed(
  () => store.getSelectTypes.items.includes(blockingTypeEnum.SHOW),
);

const hasMatchEvents = computed(() => store.getEventsState.items.length);
const hasSubscriptionEvents = computed(() => store.getSubscriptionsState.items.length);
const hasPerfomanceEvents = computed(() => store.getPerformancesState.items.length);
const hasShowsEvents = computed(() => store.getShowsState.items.length);

const isNotEmpty = computed(() => ((hasMatchEvents.value && isShowEventsEnabled.value) || isLoadingEvents.value)
|| ((hasSubscriptionEvents.value && isShowSubscriptionsEnabled.value) || isLoadingSubscription.value)
|| ((hasPerfomanceEvents.value && isShowPerfomancesEnabled.value) || isLoadingPerfomance.value)
|| ((hasShowsEvents.value && isShowShowsEnabled.value) || isLoadingShows.value));

</script>

<template>
  <div class="i-events">
    <h4 class="i-events__title">Афиша</h4>
    <EventsFilter />
    <div class="i-events__list">
      <IMatchEventsSkeleton v-if="isLoadingEvents" />

      <div v-if="isNotEmpty">
        <div v-if="isShowEventsEnabled">
          <IEventsListCol
            v-if="!isLoadingEvents && hasMatchEvents"
            :show-load-more-btn="store.getEventsState.showLoadMore"
            title="Спорт"
            :events="store.getEventsState.items"
            :is-loading-more-data="isLoadingMoreEvent"
            @load-more-events="store.loadMoreEvents"
          >
            <template #default="{ event }">
              <IMatchEvent
                type="Спорт"
                :date="event.startTimestampStr"
                :first-team-name="event?.participants?.team?.homeTeam?.name"
                :first-team-image="event?.participants?.team?.homeTeam?.imageUrl"
                :second-team-name="event?.participants?.team?.guestTeam?.name"
                :second-team-image="event?.participants?.team?.guestTeam?.imageUrl"
                :to="!event?.settings?.isSoon ? event.pageLink : ''"
                :is-home-logo-with-opponent="event?.settings?.isHomeLogoWithOpponent"
                :is-home-logo-without-opponent="event?.settings?.isHomeLogoWithoutOpponent"
                :is-show-tournament="event?.settings?.isShowTournament"
                :name="event?.name"
              />
            </template>
          </IEventsListCol>
          <IMatchEventsSkeleton v-if="loadingData" />
        </div>
        <div v-if="isShowPerfomancesEnabled">
          <IEventsListRow
            v-if="!isLoadingPerfomance && hasPerfomanceEvents"
            :show-load-more-btn="store.getPerformancesState.showLoadMore"
            :events="store.getPerformancesState.items"
            title="Концерты"
            :is-loading-more-data="isLoadingMorePerfomance"
            @load-more-events="store.loadMorePerformances"
          >
            <template #default="{ event }">
              <IConcert
                :name="event.name"
                :image="event.participants.singular.imageUrl"
                type="Концерт"
                :date="event.startTimestampStr"
                :to="!event?.settings?.isSoon ? event.pageLink : ''"
                color="#FF4A00"
              />
            </template>
          </IEventsListRow>
          <IConcertSkeleton v-if="loadingData" />
        </div>

        <!-- SHOW -->
        <div v-if="isShowShowsEnabled">
          <IEventsListRow
            v-if="!isLoadingShows && hasShowsEvents"
            :show-load-more-btn="store.getShowsState.showLoadMore"
            :events="store.getShowsState.items"
            title="Шоу"
            :is-loading-more-data="isLoadingMoreShows"
            @load-more-events="store.loadMoreShows"
          >
            <template #default="{ event }">
              <IConcert
                :name="event.name"
                :image="event?.participants?.singular?.imageUrl"
                type="Шоу"
                :date="event.startTimestampStr"
                :to="!event?.settings?.isSoon ? event.pageLink : ''"
                color="#E02022"
              />
            </template>
          </IEventsListRow>
          <IConcertSkeleton v-if="loadingData" />
        </div>

        <div v-if="isShowSubscriptionsEnabled">
          <IEventsListCol
            v-if="!isLoadingSubscription && hasSubscriptionEvents"
            :show-load-more-btn="store.getSubscriptionsState.showLoadMore"
            :events="store.getSubscriptionsState.items"
            title="Абонементы"
            :is-loading-more-data="isLoadingMoreSubscription"
            @load-more-events="store.loadMoreEvents"
          >
            <template #default="{ event }">
              <ISubscription
                type="Абонемент"
                :name="event.name"
                :description="event.participants.singular.name"
                :image="event.participants.singular.imageUrl"
                :to="!event?.settings?.isSoon ? event.pageLink : ''"
              />
            </template>
          </IEventsListCol>
          <ISubscriptionSkeleton v-if="loadingData" />
        </div>
      </div>

      <div v-else class="i-events__empty-list">
        Сейчас нет планируемых мероприятий
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.i-events{
  display: flex;
  flex-direction: column;
  gap: 30px;

  &__title {
    font-size: 50px;
    font-weight: 700;
  }

  &__empty-list {
    color: $grey-text;
    text-align: center;
    font-size: 28px;
    font-weight: 700;
  }

  @media screen and (max-width: $screen-md-min) {
    &__title {
      font-size: 40px;
      color: $deep-gray-blue;
    }
  }
}
</style>